"use client"

import { HttpTypes } from "@medusajs/types"
import { Button, Container } from "@/components/common"
import Image from "next/image"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"

type ImageGalleryProps = {
  images: HttpTypes.StoreProductImage[]
  title: string
}

const ImageGallery = ({ images, title }: ImageGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true })
  const [emblaThumbRef, emblaThumbApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  })

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbApi) return
      emblaMainApi.scrollTo(index)
    },
    [emblaMainApi, emblaThumbApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbApi) return
    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbApi.scrollTo(emblaMainApi.selectedScrollSnap())
  }, [emblaMainApi, emblaThumbApi])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()
    emblaMainApi.on("select", onSelect)
    emblaMainApi.on("reInit", onSelect)
  }, [emblaMainApi, onSelect])

  // Fallback for single image or no images
  if (!images || images.length === 0) return null

  if (images.length === 1) {
    return (
      <div className="flex items-start relative">
        <Container className="relative aspect-square w-full overflow-hidden bg-surface-subtle rounded-xl">
          {!!images[0].url && (
            <Image
              src={images[0].url}
              priority
              className="absolute inset-0 rounded-xl"
              alt={title}
              fill
              sizes="(max-width: 576px) 100vw, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
              style={{ objectFit: "cover" }}
            />
          )}
        </Container>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Main carousel */}
      <div className="overflow-hidden rounded-xl" ref={emblaMainRef}>
        <div className="flex">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="relative aspect-square min-w-0 flex-[0_0_100%]"
            >
              {!!image.url && (
                <Image
                  src={image.url}
                  priority={index <= 1}
                  className="absolute inset-0 rounded-xl object-cover transition-transform duration-300 hover:scale-105"
                  alt={`${title} — image ${index + 1}`}
                  fill
                  sizes="(max-width: 576px) 100vw, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="overflow-hidden" ref={emblaThumbRef}>
        <div className="flex gap-2">
          {images.map((image, index) => (
            <Button
              variant="ghost"
              key={image.id}
              onClick={() => onThumbClick(index)}
              type="button"
              className={`h-auto p-0 rounded-none font-normal relative aspect-square min-w-0 flex-[0_0_calc(20%-6px)] rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                index === selectedIndex
                  ? "border-primary opacity-100 shadow-sm"
                  : "border-transparent opacity-60 hover:opacity-90"
              }`}
            >
              {!!image.url && (
                <Image
                  src={image.url}
                  className="absolute inset-0 rounded-lg object-cover"
                  alt={`${title} — thumbnail ${index + 1}`}
                  fill
                  sizes="100px"
                />
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ImageGallery
